import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Radio,
  ListItemText,
  Collapse,
  Tooltip
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import SearchRounded from "@mui/icons-material/SearchRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ArrowDropDownRounded from "@mui/icons-material/ArrowDropDownRounded";
import ReorderRounded from "@mui/icons-material/ReorderRounded";
import ViewColumnRounded from "@mui/icons-material/ViewColumnRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";

import type { Bot } from "@/data/mock-data";
import { deriveBotReportMetrics } from "@/data/mock-data";

// ---- Public types + helpers ----------------------------------------------

export type BotsViewMode = "simple" | "advanced";

/**
 * Preset chips — a "filter pill" row on the
 * web report. Each preset maps to a pure predicate applied after the
 * search + base filters. Only one preset can be active at a time —
 * combining "Gainers" with "In Drawdown" is either contradictory or
 * redundant depending on the pair, and UI-wise a radio-style chip row
 * reads cleaner than arbitrary multi-select boolean algebra. Users who
 * want "Sharpe ≥ 1 and In Drawdown" can combine sort + a single
 * preset, which covers the realistic scanning cases.
 */
export type BotPreset =
  | "gainers"
  | "losers"
  | "active"
  | "sharpe_ge_1"
  | "sharpe_ge_2"
  | "sharpe_lt_05"
  | "in_drawdown"
  | "young"
  | "mature"
  | "top10_contrib";

export type BotSortKey =
  | "value"
  | "dayPct"
  | "twrPct"
  | "apyPct"
  | "sharpe"
  | "maxDDPct"
  | "netDeposits"
  | "contribPct"
  | "ageDays"
  | "allocPct";

export interface BotsFilterState {
  mode: BotsViewMode;
  search: string;
  /** `null` = no preset active. Only one preset is ever set. */
  preset: BotPreset | null;
  sortKey: BotSortKey;
  sortDir: "asc" | "desc";
}

export const DEFAULT_BOTS_FILTER: BotsFilterState = {
  mode: "simple",
  search: "",
  preset: null,
  // "Value" sort mirrors the web report default — the user expects
  // the biggest positions at the top when they first open the tab.
  sortKey: "value",
  sortDir: "desc"
};

// ---- Option tables -------------------------------------------------------

// Type alias for the inferred derived record — pulls it out of the Map
// return type so the preset factories can lean on it without importing
// a separate type from mock-data. Each preset's factory receives this
// map so it can look up `ageDays` / `contribPct` without recomputing.
type Derived = NonNullable<ReturnType<typeof deriveBotReportMetrics> extends Map<string, infer V> ? V : never>;

const PRESETS: ReadonlyArray<{
  value: BotPreset;
  label: string;
  build: (bots: Bot[], derived: Map<string, Derived>) => (b: Bot) => boolean;
}> = [
  {
    value: "gainers",
    label: "Gainers",
    build: () => (b) => b.twrPct > 0
  },
  {
    value: "losers",
    label: "Losers",
    build: () => (b) => b.twrPct < 0
  },
  {
    value: "active",
    label: "Active",
    build: () => (b) => b.status === "ACTIVE"
  },
  {
    value: "sharpe_ge_1",
    label: "Sharpe ≥ 1",
    build: () => (b) => b.sharpe >= 1
  },
  {
    value: "sharpe_ge_2",
    label: "Sharpe ≥ 2",
    build: () => (b) => b.sharpe >= 2
  },
  {
    value: "sharpe_lt_05",
    label: "Sharpe < 0.5",
    build: () => (b) => b.sharpe < 0.5
  },
  {
    value: "in_drawdown",
    label: "In drawdown",
    // Heuristic: currentValue below allocatedAmount (user-facing "down
    // on original capital") OR TWR negative. Either catches a cohort
    // worth investigating even though a *true* "in active drawdown"
    // flag would need a running peak from the sparkline.
    build: () => (b) => b.currentValue < b.allocatedAmount || b.twrPct < 0
  },
  {
    value: "young",
    label: "Young",
    // Under 60 days old — freshly deployed, caveat-emptor on stats.
    build: (_bots, d) => (b) => (d.get(b.id)?.ageDays ?? 0) < 60
  },
  {
    value: "mature",
    label: "Mature",
    // 180+ days — long enough that the APY extrapolation starts to be
    // trustworthy.
    build: (_bots, d) => (b) => (d.get(b.id)?.ageDays ?? 0) >= 180
  },
  {
    value: "top10_contrib",
    label: "Top 10 by contrib.",
    // Pick the top-10 by contribPct within the cohort. The factory
    // pre-computes the allow-list so the predicate is O(1).
    build: (bots, d) => {
      const ranked = [...bots]
        .map((b) => [b.id, d.get(b.id)?.contribPct ?? 0] as const)
        .sort((a, bb) => bb[1] - a[1])
        .slice(0, 10)
        .map(([id]) => id);
      const allow = new Set(ranked);
      return (b) => allow.has(b.id);
    }
  }
];

const SORT_OPTIONS: ReadonlyArray<{ value: BotSortKey; label: string }> = [
  { value: "value", label: "Value" },
  { value: "dayPct", label: "Day %" },
  { value: "twrPct", label: "TWR" },
  { value: "apyPct", label: "APY" },
  { value: "sharpe", label: "Sharpe" },
  { value: "maxDDPct", label: "Max DD" },
  { value: "netDeposits", label: "Net deposits" },
  { value: "contribPct", label: "Contrib %" },
  { value: "allocPct", label: "Alloc %" },
  { value: "ageDays", label: "Age" }
];

// ---- Pure filter + sort ---------------------------------------------------

/**
 * Apply search + preset + sort to a bot list. Pure — consumers lift
 * the filter state and memoise with `useMemo` keyed on
 * `[bots, filter]`.
 *
 * We compute the derived metrics once here (same cohort the view is
 * about to render) so the preset predicates + sort can lean on
 * `allocPct` / `contribPct` / `ageDays` without re-deriving per row.
 */
export function applyBotsFilter(
  bots: Bot[],
  filter: BotsFilterState
): Bot[] {
  const q = filter.search.trim().toLowerCase();
  const derived = deriveBotReportMetrics(bots);

  // Search — name OR description OR any ticker substring. Case-insensitive.
  let out = bots.filter((b) => {
    if (!q) return true;
    return (
      b.name.toLowerCase().includes(q) ||
      b.description.toLowerCase().includes(q) ||
      b.tickers.some((t) => t.toLowerCase().includes(q))
    );
  });

  // Preset predicate layered on top.
  if (filter.preset) {
    const spec = PRESETS.find((p) => p.value === filter.preset);
    if (spec) {
      const pred = spec.build(out, derived);
      out = out.filter(pred);
    }
  }

  // Sort by the selected key. Derived values (ageDays, contribPct,
  // allocPct) are looked up in the map; everything else reads
  // directly off the bot. Secondary sort by name keeps deterministic
  // ordering when two bots tie on the primary key.
  const dir = filter.sortDir === "asc" ? 1 : -1;
  out.sort((a, b) => {
    const av = extractSortValue(a, filter.sortKey, derived);
    const bv = extractSortValue(b, filter.sortKey, derived);
    if (av === bv) return a.name.localeCompare(b.name);
    return av < bv ? -1 * dir : 1 * dir;
  });
  return out;
}

function extractSortValue(
  b: Bot,
  key: BotSortKey,
  derived: Map<string, Derived>
): number {
  switch (key) {
    case "value":
      return b.currentValue;
    case "dayPct":
      return b.dayChangePct;
    case "twrPct":
      return b.twrPct;
    case "apyPct":
      return b.apyPct;
    case "sharpe":
      return b.sharpe;
    case "maxDDPct":
      return b.maxDDPct;
    case "netDeposits":
      return b.netDeposits;
    case "contribPct":
      return derived.get(b.id)?.contribPct ?? 0;
    case "allocPct":
      return derived.get(b.id)?.allocPct ?? 0;
    case "ageDays":
      return derived.get(b.id)?.ageDays ?? 0;
  }
}

// ---- Component -----------------------------------------------------------

interface BotsFilterBarProps {
  filter: BotsFilterState;
  onChange: (next: BotsFilterState) => void;
  /** Extra styles on the outer Box. */
  sx?: SxProps<Theme>;
}

/**
 * Filter bar for the Bots tab — layout mirrors OrdersFilterBar:
 *   1. Simple / Advanced segmented toggle (centered).
 *   2. Search input, full width.
 *   3. Preset chip row — scrollable, radio-style. Tapping an active
 *      chip clears it; tapping a new one replaces.
 *   4. Sort chip — opens a single-select menu of sort keys, with a
 *      separate direction toggle inside the menu.
 *
 * Sort lives at the end of the chip row rather than as a standalone
 * control so the filter "bar" stays one row tall on mobile.
 */
export function BotsFilterBar({ filter, onChange, sx }: BotsFilterBarProps) {
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);

  // Filter-disclosure mirrors the Orders tab: chip rail starts collapsed,
  // auto-opens whenever a filter is active (preset selected, custom sort,
  // or non-empty search). Tap-Tune-while-open resets the filter back to
  // defaults — same shortcut Sergey gave Orders 2026-04-25.
  const anyFilterActive =
    filter.search.trim().length > 0 ||
    filter.preset !== null ||
    filter.sortKey !== DEFAULT_BOTS_FILTER.sortKey ||
    filter.sortDir !== DEFAULT_BOTS_FILTER.sortDir;
  const [filtersOpen, setFiltersOpen] = useState<boolean>(anyFilterActive);
  useEffect(() => {
    if (anyFilterActive && !filtersOpen) setFiltersOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyFilterActive]);

  function toggleFiltersDisclosure() {
    if (filtersOpen) {
      setFiltersOpen(false);
      onChange({
        ...DEFAULT_BOTS_FILTER,
        // Preserve view choice (Simple/Advanced) — that's a layout
        // pref, not a filter — same convention as OrdersFilterBar.
        mode: filter.mode
      });
    } else {
      setFiltersOpen(true);
    }
  }

  const sortLabel = useMemo(() => {
    const opt = SORT_OPTIONS.find((o) => o.value === filter.sortKey);
    const base = opt ? opt.label : "Sort";
    return `${base} ${filter.sortDir === "desc" ? "↓" : "↑"}`;
  }, [filter.sortKey, filter.sortDir]);

  const togglePreset = (p: BotPreset) => {
    onChange({ ...filter, preset: filter.preset === p ? null : p });
  };

  const pickSort = (key: BotSortKey) => {
    // Tapping the *current* key flips direction rather than no-op —
    // matches spreadsheet/table-header expectations.
    if (filter.sortKey === key) {
      onChange({
        ...filter,
        sortDir: filter.sortDir === "asc" ? "desc" : "asc"
      });
    } else {
      // Switching to a new key — default to `desc` for everything
      // except Max DD and Age where ascending is the more useful view
      // ("worst drawdowns first" / "oldest first" would bury the
      // interesting rows).
      const defaultDir: "asc" | "desc" =
        key === "maxDDPct" || key === "ageDays" ? "asc" : "desc";
      onChange({ ...filter, sortKey: key, sortDir: defaultDir });
    }
  };

  return (
    <Box sx={sx}>
      {/* Single always-visible row: search + view-toggle + filter
       *  disclosure. Mirrors the Orders tab exactly — Simple/Advanced
       *  segmented control collapsed into Reorder/ViewColumn icons,
       *  and the preset chip rail hides behind the Tune disclosure
       *  icon. (Sergey 2026-04-26.) */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ mb: filtersOpen ? 1.25 : 0 }}
      >
        <TextField
          size="small"
          placeholder="Search name"
          value={filter.search}
          onChange={(e) => onChange({ ...filter, search: e.target.value })}
          // Focusing search auto-discloses the chip rail so search +
          // filter facets surface together — matches Orders pattern.
          onFocus={() => {
            if (!filtersOpen) setFiltersOpen(true);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: filter.search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => onChange({ ...filter, search: "" })}
                  sx={{ p: 0.25 }}
                >
                  <CloseRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
            sx: {
              borderRadius: 2,
              bgcolor: (t) => t.palette.action.hover,
              "& fieldset": { border: "none" },
              fontSize: "1rem",
              height: 44
            }
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />

        {/* Simple view (3 horizontal bars) */}
        <Tooltip title="Simple view">
          <IconButton
            size="small"
            aria-label="Simple view"
            onClick={() => {
              if (filter.mode !== "simple") onChange({ ...filter, mode: "simple" });
            }}
            sx={{
              color: filter.mode === "simple" ? "primary.main" : "text.secondary",
              bgcolor: (t) =>
                filter.mode === "simple" ? t.palette.action.selected : "transparent"
            }}
          >
            <ReorderRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
        {/* Advanced (3 vertical columns) */}
        <Tooltip title="Advanced (table) view">
          <IconButton
            size="small"
            aria-label="Advanced view"
            onClick={() => {
              if (filter.mode !== "advanced") onChange({ ...filter, mode: "advanced" });
            }}
            sx={{
              color: filter.mode === "advanced" ? "primary.main" : "text.secondary",
              bgcolor: (t) =>
                filter.mode === "advanced" ? t.palette.action.selected : "transparent"
            }}
          >
            <ViewColumnRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>

        {/* Tune — disclose / hide chip rail. Tap-while-open also resets
         *  filters back to defaults (preserves Simple/Advanced choice). */}
        <Tooltip title={filtersOpen ? "Hide and clear filters" : "Show filters"}>
          <IconButton
            size="small"
            aria-label={filtersOpen ? "Hide and clear filters" : "Show filters"}
            onClick={toggleFiltersDisclosure}
            sx={{
              color:
                filtersOpen || anyFilterActive ? "primary.main" : "text.secondary",
              bgcolor: (t) =>
                filtersOpen ? t.palette.action.selected : "transparent"
            }}
          >
            <TuneRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Chip rail — disclosed via Collapse. Same scroll/scrollbar-hide
       *  treatment as the Orders chip rail. */}
      <Collapse in={filtersOpen} timeout={200} unmountOnExit>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: "auto",
            pb: 0.5,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none"
          }}
        >
          {PRESETS.map((p) => (
            <PresetChip
              key={p.value}
              label={p.label}
              active={filter.preset === p.value}
              onClick={() => togglePreset(p.value)}
            />
          ))}
          <Box sx={{ flex: 1, minWidth: 12 }} />
          <PresetChip
            label={sortLabel}
            active
            showCaret
            onClick={(e) => setSortAnchor(e.currentTarget)}
          />
        </Stack>
      </Collapse>

      <Menu
        anchorEl={sortAnchor}
        open={Boolean(sortAnchor)}
        onClose={() => setSortAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {SORT_OPTIONS.map((o) => (
          <MenuItem
            key={o.value}
            dense
            onClick={() => {
              pickSort(o.value);
              // Keep the menu open on direction-toggle taps — feels
              // more natural than a forced close when the user is
              // dialing in the sort. Close on an actual key change.
              if (filter.sortKey !== o.value) setSortAnchor(null);
            }}
          >
            <Radio
              edge="start"
              size="small"
              checked={filter.sortKey === o.value}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText
              primary={o.label}
              secondary={
                filter.sortKey === o.value
                  ? filter.sortDir === "desc"
                    ? "Highest first (tap to flip)"
                    : "Lowest first (tap to flip)"
                  : undefined
              }
              primaryTypographyProps={{ variant: "body2" }}
              secondaryTypographyProps={{
                variant: "caption",
                color: "text.secondary"
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

// ---- Internals -----------------------------------------------------------

/**
 * Preset chip — behaves like the one in OrdersFilterBar but without an
 * inline × (presets are radio-style, so clicking an active chip toggles
 * it off instead of needing a separate clear affordance). Sort chip
 * reuses the same component with `showCaret` on.
 */
function PresetChip({
  label,
  active,
  showCaret,
  onClick
}: {
  label: string;
  active: boolean;
  showCaret?: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  return (
    <Chip
      label={
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          sx={{ minWidth: 0 }}
        >
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {label}
          </Box>
          {showCaret && (
            <ArrowDropDownRounded sx={{ fontSize: 18, ml: 0.25 }} />
          )}
        </Stack>
      }
      clickable
      onClick={onClick}
      size="small"
      sx={{
        flexShrink: 0,
        borderRadius: 999,
        px: 0.5,
        fontWeight: 500,
        // Apr-2026 typography sweep: body1 (1rem) + proportional height
        // bump — preset chips were markedly smaller than the Simple/
        // Advanced toggle, which made the row read as secondary.
        fontSize: "1rem",
        height: 36,
        bgcolor: (t) =>
          active
            ? t.palette.mode === "dark"
              ? "rgba(144,202,249,0.18)"
              : "rgba(25,118,210,0.12)"
            : t.palette.action.hover,
        color: active ? "primary.main" : "text.primary",
        "&:hover": {
          bgcolor: (t) =>
            active
              ? t.palette.mode === "dark"
                ? "rgba(144,202,249,0.26)"
                : "rgba(25,118,210,0.20)"
              : t.palette.action.selected
        }
      }}
    />
  );
}
